import { FC } from "react";
import styled from "styled-components";
import DirectorSignature from "assets/images/director-signature-2.png";

const LetterFromDirectorDiv = styled.div`
  display: block;
  width: 789px;
  max-width: 100%;
  padding: 150px 20px;
  margin: 0 auto;
  color: #fff;
  padding-bottom: 100px;
  font-family: "Neue-Haas";

  h2 {
    font-size: 30px;
    line-height: 1.6;
    font-weight: 700;
    text-align: center;
    text-transform: uppercase;
    margin-bottom: 50px;
    font-family: "new-order";
  }

  h4 {
    font-weight: 700;
    font-size: 19px;
    margin-bottom: 25px;
  }

  .text {
    color: #cecfd0;
    font-size: 16px;
    line-height: 26px;
    letter-spacing: 2%;
  }

  span {
    display: block;
    font-size: 12px;
    font-weight: 700;
    margin-top: 15px;
  }

  a {
    color: #fff;
    text-decoration: underline;
  }

  @media ${({ theme }) => theme.mediaQueries.bellowTabletPortrait} {
    padding: 50px 20px;
  }

  .signature {
    max-width: 226px;
    height: auto;
  }
`;

const LetterFromDirector: FC = () => {
  return (
    <LetterFromDirectorDiv id="letter-from-director">
      <h2>A Letter from our Director</h2>

      <h4>2025 776 Fellowship Year in Review: A New Era of Climate and Frontier Innovation</h4>

      <div className="text">
        <p>
          As 2025 has come to a close, the intersection of climate and technology has never been more dynamic or more
          urgent. From AI-designed satellites to carbon-zero rare earths, the frontiers of climate innovation are
          expanding in ways that were barely imaginable just a few years ago. At the 776 Foundation, we are proud to
          stand behind the young founders at the heart of this movement: our community of 80 Climate Fellows, who are
          building bold solutions for a livable and equitable planet.
        </p>

        <p>
          The 776 Fellowship continues to provide $100,000 funding over two years to founders aged 18–24, empowering
          them to dedicate their full energy to solving the defining challenge of our time. Together, this growing
          community of innovators is reimagining how we power, produce, and protect our world.
        </p>

        <p>
          Our 2025 cohort brings with it a wave of ambition and technical brilliance, leaders who are proving that the
          climate solutions of tomorrow will be as scientific as they are systemic.
        </p>

        <p>
          Among them are fellows like{" "}
          <a href="https://www.forbes.com/profile/solcoa-industries/" target="_blank" rel="noopener noreferrer">
            Hooman Reza Nezhad
          </a>
          , co-founder and CEO of{" "}
          <a href="https://solcoaindustries.com/" target="_blank" rel="noopener noreferrer">
            Solcoa Industries
          </a>
          , who is tackling one of the least discussed yet most critical bottlenecks in the clean energy transition:
          rare earth metal production. Having once researched microplastics and even Martian soil extraction techniques,
          Hooman is now scaling carbon-zero rare earth manufacturing in the West, replacing toxic, emissions-heavy
          processes with clean, circular chemistry. With a recent round of seed funding, his work is setting a precedent
          for how we can decarbonize industrial foundations while rebuilding resilient supply chains for the energy
          transition in the US.
        </p>

        <p>
          <a href="https://www.forbes.com/profile/sophia-xu/" target="_blank" rel="noopener noreferrer">
            Sophia Xu
          </a>
          , Co-Founder and Chief Scientific Officer of{" "}
          <a href="https://www.carbonbridge.io/" target="_blank" rel="noopener noreferrer">
            CarbonBridge
          </a>
          , is harnessing the power of biology to reimagine industrial production. Her team's gas fermentation platform
          converts waste gasses into fuels and chemicals, effectively transforming emissions into value.
        </p>

        <p>
          From the ground to orbit,{" "}
          <a href="https://www.linkedin.com/in/jrod2024/" target="_blank" rel="noopener noreferrer">
            Jacob Rodriguez
          </a>
          , founder and CEO of{" "}
          <a href="https://www.oligospace.com/" target="_blank" rel="noopener noreferrer">
            Oligo Space
          </a>
          , is using AI to push the boundaries of climate and aerospace innovation. His company develops AI-driven
          design automation for spacecraft systems, making it possible to produce modular, origami-inspired satellites
          that can rapidly deploy for earth observation, environmental monitoring, and disaster response. By advancing
          sustainable, scalable space infrastructure, Jacob and his team are redefining how we leverage space as a tool
          for climate resilience.
        </p>

        <p>
          And continuing the momentum from our 2024 cohort,{" "}
          <a href="https://www.linkedin.com/in/ellacsuka/" target="_blank" rel="noopener noreferrer">
            Ella Csuka
          </a>
          , founder and CEO of{" "}
          <a href="https://www.ecotuneinc.com/" target="_blank" rel="noopener noreferrer">
            Ecotune
          </a>
          , is pioneering bio-inspired material science to reshape industries from fashion to automotive. Backed by the
          NSF and EPA, Ecotune's bio-based polymer technologies are setting new standards for sustainability and
          performance.
        </p>

        <p>
          As we reflect on 2025, the breadth of innovation within our fellowship is both humbling and exhilarating. Our
          fellows are not only advancing decarbonization but are reengineering the systems that underpin it from atoms
          to algorithms, molecules to orbits. Many are leveraging AI not just as a tool, but as a force multiplier for
          discovery, accelerating material design, optimizing energy systems, and decoding the planet's most complex
          data in real time.
        </p>

        <p>
          To our President, Alexis Ohanian, thank you for the trust and partnership that make this work possible. To our
          community of fellows: your creativity, grit, and relentless optimism continue to prove that youth-led
          innovation isn't the future, it's already here.
        </p>

        <p>
          As we look ahead to 2026, one thing is clear: this community is not waiting for permission to build. Together,
          our fellows are charting a bold path for planet-positive innovation, inspiring the world to act with the same
          urgency and conviction.
        </p>

        <p>Empowering Today's Youth for Tomorrow's World</p>
      </div>

      <img src={DirectorSignature} className="signature" alt="Fellowship Director's signature" />

      <span>Elisabeth Garvin</span>
    </LetterFromDirectorDiv>
  );
};

export default LetterFromDirector;
